import { QUERY_KEY } from '@constants';
import { useQueryClient } from '@tanstack/react-query';
import type { Space } from '@viewup/ui';
import { Box, Column, getColorValue, Row, Typography, theme } from '@viewup/ui';
import { AppOnlyGuideModal } from 'app/_components/AppOnlyGuideModal';
import { useMarkAsReadNotification } from 'app/notifications/_features/api';
import { isWebRoutableLink } from 'app/notifications/_features/lib/isWebRoutableLink';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import type { GetNotificationListResponseDataDto } from 'viewup-api/v2';

interface NotificationItemProps extends GetNotificationListResponseDataDto {
  itemPadding?: keyof Space;
}

export const NotificationItem = (props: NotificationItemProps) => {
  const { itemPadding, notificationUuid, createdAt, isRead, content, linkUrl } = props;
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isMobileOnlyModalOpen, setIsMobileOnlyModalOpen] = useState(false);

  const { mutateAsync: markAsRead } = useMarkAsReadNotification({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.NOTIFICATIONS],
        });
      },
    },
  });

  const changeReadState = async () => {
    // 웹에 대응 페이지가 없는 linkUrl이면 라우팅 대신 모바일 안내 모달을 띄운다. 읽지 않은
    // 알림이면 읽음 처리는 그대로 수행하되(이미 읽은 알림이면 하지 않는다), 라우팅은 하지 않는다.
    if (linkUrl && !isWebRoutableLink(linkUrl)) {
      if (!isRead) {
        await markAsRead({ notificationUuids: [notificationUuid] });
      }
      setIsMobileOnlyModalOpen(true);
      return;
    }

    if (isRead) return;

    await markAsRead({ notificationUuids: [notificationUuid] });

    if (linkUrl) router.push(linkUrl);
  };

  return (
    <Column
      css={{
        borderBottom: `1px solid ${getColorValue('border.tertiary', theme)}`,
      }}
      gap={4}
      key={notificationUuid}
      onClick={changeReadState}
      py={itemPadding || 16}
      width={'100%'}
    >
      <Row justifyContent='space-between' width={'100%'}>
        <Typography color='text.disabled' fontType='caption.regular'>
          {dayjs(createdAt).format('YYYY.MM.DD HH:mm')}
        </Typography>
        {!isRead && <Box bgColor='bg.danger' br={8} height={6} width={6} />}
      </Row>
      <Typography color='text.secondary' fontType='caption.regular'>
        {content}
      </Typography>
      <AppOnlyGuideModal
        isOpen={isMobileOnlyModalOpen}
        onClose={() => setIsMobileOnlyModalOpen(false)}
        predicate='확인할 수 있어요!'
        subject='이 알림은'
      />
    </Column>
  );
};
